/* eslint-disable no-unused-vars */
/* eslint-disable no-loop-func */
import React, { useEffect, useState, useRef, useMemo } from "react";
import { Doughnut } from "react-chartjs-2";
import { locales } from "../../locales";
import CoalitionCheckbox from "./CoalitionCheckbox";

// Function to generate a random hex color
const getRandomColor = () =>
  "#" + Math.floor(Math.random() * 16777215).toString(16);

function CoalitionBuilder({ data, langId }) {
  const [selectedParties, setSelectedParties] = useState([]);

  const coloredData = useMemo(
    () =>
      data.map((item) => {
        if (item.color === undefined) {
          item.color = getRandomColor();
        }
        return item;
      }),
    [data]
  );

  useEffect(() => {
    // Find the index of the party with the highest percentage (votes)
    const highestVotesIndex = data.reduce(
      (highestIndex, currentParty, currentIndex, array) =>
        currentParty.percentage > array[highestIndex].percentage
          ? currentIndex
          : highestIndex,
      0
    );

    // Set the state for this party to true, and for all others to false
    setSelectedParties(data.map((_, index) => index === highestVotesIndex));
  }, [data]);

  const chartRef = useRef(null);

  const handlePartySelection = (index) => {
    setSelectedParties((prev) =>
      prev.map((selected, i) => (i === index ? !selected : selected))
    );
  };

  const selectedData = useMemo(
    () => data.filter((_, i) => selectedParties[i]),
    [data, selectedParties]
  );

  const totalPercentage = useMemo(
    () => selectedData.reduce((sum, { percentage }) => sum + percentage, 0),
    [selectedData]
  );

  const remainingPercentage = useMemo(
    () => 100 - totalPercentage,
    [totalPercentage]
  );

  useEffect(() => {
    if (chartRef.current) {
      const updatedData = {
        labels: [...selectedData.map(({ party }) => party), "Remaining"],
        datasets: [
          {
            data: [
              ...selectedData.map(({ percentage }) => percentage),
              remainingPercentage,
            ],
            backgroundColor: [
              ...selectedData.map(({ color }) => color),
              "#F4F4F4",
            ],
          },
        ],
      };

      chartRef.current.data = updatedData;
      chartRef.current.update();
    }
  }, [chartRef, selectedData, remainingPercentage]);

  const chartData = {
    labels: [...selectedData.map(({ party }) => party), "Remaining"],
    datasets: [
      {
        label: locales[langId].votesPercentage,
        data: [
          ...selectedData.map(({ percentage }) => percentage),
          remainingPercentage,
        ],
        backgroundColor: [...selectedData.map(({ color }) => color), "#F4F4F4"],
      },
    ],
  };

  const options = {
    responsive: true,
    rotation: 270,
    circumference: 180,
    onClick: (_, activeElements) => {
      if (activeElements.length > 0) {
        const index = activeElements[0].index;
        handlePartySelection(index);
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            var label = "";
            // if (label) {
            //   label += ": ";
            // }
            if (context.parsed !== null) {
              label += context.parsed.toFixed(2) + "%";
            }
            return label;
          },
        },
      },
    },
  };
  return (
    <div className="flex flex-col justify-between wort-medium:flex-row">
      <div
        className="flex flex-col justify-center items-center h-[300px] w-full
      wort-medium:w-1/2 border rounded-[4px] pb-2"
      >
        <Doughnut data={chartData} options={options} ref={chartRef} />
        <div className="flex flex-col justify-center mx-4 w-full h-[70px] wort-medium:w-[298px] wort-medium:-mt-12">
          <div className="h-22 w-full relative">
            <div className="absolute top-0 bottom-0 left-0 right-0 flex">
              {selectedData.map((party, index) => (
                <div
                  key={index}
                  className="flex-grow"
                  style={{
                    width: `${party.percentage}%`,
                    backgroundColor: party.color,
                  }}
                ></div>
              ))}
              <div
                className="flex-grow bg-wort-background"
                style={{
                  width: `${remainingPercentage}%`,
                }}
              ></div>
              <div
                className="absolute left-1/2 w-1 h-14 bg-black transform -translate-y-1/2"
                style={{ width: 0.8, top: "10px" }}
              ></div>
            </div>
          </div>
          <div className="flex justify-center mt-3">
            <span className="text-xs mt-1">50%</span>
          </div>
        </div>
      </div>
      <div className="flex flex-row mt-10 wort-medium:w-1/2 w-full wort-medium:pl-10">
        {[...Array(2)].map((_, columnIndex) => (
          <div
            key={columnIndex}
            className={`flex flex-col w-full wort-medium:justify-center ${
              columnIndex === 0 ? "mr-2" : "ml-2"
            }`}
          >
            {data
              .slice(columnIndex * 4, columnIndex * 4 + 4)
              .map((party, innerIndex) => {
                const actualIndex = columnIndex * 4 + innerIndex;
                return (
                  <div key={actualIndex} className="flex items-center mb-2">
                    <CoalitionCheckbox
                      checked={selectedParties[actualIndex]}
                      onChange={() => handlePartySelection(actualIndex)}
                      className="mr-2 h-8 w-8"
                    />
                    <div
                      className="w-29 h-22"
                      style={{ backgroundColor: party.color }}
                    ></div>
                    <span className="ml-2 text-lg leading-5">
                      {party.party}
                    </span>
                  </div>
                );
              })}
          </div>
        ))}
      </div>
    </div>
  );
}
export default React.memo(CoalitionBuilder);
